import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateRole, useUpdateRole, useAvailableRoles } from '@/hooks/useAdmin';
import { useToast } from '@/hooks/useToast';
import type { Role, RoleSummary } from '@/types';

interface RoleModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // The Role Management list passes a `RoleSummary` row; the modal reads only
  // id/name/description, which both shapes carry (#1455).
  role?: Role | RoleSummary | null;
}

/**
 * Sentinel for the "no parent role" option (#1490).
 *
 * Radix's `Select` throws if a `SelectItem` carries `value=""` — the empty
 * string is reserved for "cleared selection, show the placeholder". This option
 * used to be `<SelectItem value="">`, and because the throw happens during
 * render it escaped the dialog and took the whole `/roles` route down to the
 * router error boundary, so neither Create Role nor Edit was reachable at all.
 *
 * Anything that leaves this component must map the sentinel back to
 * `undefined` — see `parentRoleIdForSubmit`.
 */
const NO_PARENT_ROLE_VALUE = '__no_parent__';

export const RoleModal: React.FC<RoleModalProps> = ({
  open,
  onOpenChange,
  role,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentRoleId, setParentRoleId] = useState<string>(NO_PARENT_ROLE_VALUE);
  const [isActive, setIsActive] = useState(true);

  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const { data: rolesData } = useAvailableRoles();
  const { toast } = useToast();

  const isEditMode = !!role;

  // Reset the form when the dialog opens/closes or the edited role changes.
  // Adjusted during render (React's documented pattern for resetting state
  // from props: https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // instead of in an effect: `hasSeeded`/`seededRole`/`seededOpen` mirror what
  // the form was last seeded from. A mismatch — including the first render —
  // seeds the form synchronously and updates the mirrors, so React re-renders
  // once more before committing and the branch converges (no further updates).
  const [hasSeeded, setHasSeeded] = useState(false);
  const [seededRole, setSeededRole] = useState(role);
  const [seededOpen, setSeededOpen] = useState(open);
  if (!hasSeeded || role !== seededRole || open !== seededOpen) {
    setHasSeeded(true);
    setSeededRole(role);
    setSeededOpen(open);
    if (role) {
      setName(role.name || '');
      setDescription(role.description || '');
      setParentRoleId((role as any).parent_role_id || NO_PARENT_ROLE_VALUE);
      setIsActive((role as any).is_active !== undefined ? (role as any).is_active : true);
    } else {
      // Reset form for create mode
      setName('');
      setDescription('');
      setParentRoleId(NO_PARENT_ROLE_VALUE);
      setIsActive(true);
    }
  }

  const validateName = (roleName: string): string | null => {
    if (roleName.length < 3) {
      return 'Role name must be at least 3 characters long';
    }

    // Allow alphanumeric characters, hyphens, and underscores
    const validNamePattern = /^[a-zA-Z0-9_-]+$/;
    if (!validNamePattern.test(roleName)) {
      return 'Role name can only contain letters, numbers, hyphens, and underscores';
    }

    return null;
  };

  const validateDescription = (desc: string): string | null => {
    if (desc.length < 10) {
      return 'Description must be at least 10 characters long';
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name
    const nameError = validateName(name);
    if (nameError) {
      toast({
        title: 'Validation Error',
        description: nameError,
        variant: 'destructive',
      });
      return;
    }

    // Validate description
    const descriptionError = validateDescription(description);
    if (descriptionError) {
      toast({
        title: 'Validation Error',
        description: descriptionError,
        variant: 'destructive',
      });
      return;
    }

    // The sentinel is a UI-only value; "no parent" must go to the API as an
    // absent field, exactly as the empty string used to (#1490).
    const parentRoleIdForSubmit =
      parentRoleId === NO_PARENT_ROLE_VALUE || !parentRoleId
        ? undefined
        : parentRoleId;

    try {
      if (isEditMode && role) {
        // Update role
        await updateRole.mutateAsync({
          roleId: role.id,
          data: {
            name,
            description,
            parent_role_id: parentRoleIdForSubmit,
            is_active: isActive,
          },
        });
        toast({
          title: 'Success',
          description: 'Role updated successfully',
        });
      } else {
        // Create role
        await createRole.mutateAsync({
          name,
          description,
          parent_role_id: parentRoleIdForSubmit,
        });
        toast({
          title: 'Success',
          description: 'Role created successfully',
        });
      }
      onOpenChange(false);
    } catch (err: unknown) {
      const error = err as Error;
      toast({
        title: 'Error',
        description: error.message || `Failed to ${isEditMode ? 'update' : 'create'} role`,
        variant: 'destructive',
      });
    }
  };

  // Get available roles for parent selection (excluding the current role if editing)
  const availableRoles = (rolesData?.roles || []).filter(
    (r) => !isEditMode || r.id !== role?.id
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Role' : 'Create Role'}</DialogTitle>
            <DialogDescription>
              {isEditMode
                ? 'Update role information and settings'
                : 'Create a new role with a descriptive name'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="admin, viewer, data-engineer"
                required
              />
              <p className="text-xs text-muted-foreground">
                Min 3 characters, alphanumeric with hyphens/underscores
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose and responsibilities of this role..."
                required
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Min 10 characters
              </p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="parentRole">Parent Role (Optional)</Label>
              <Select
                value={parentRoleId}
                onValueChange={setParentRoleId}
              >
                <SelectTrigger id="parentRole">
                  <SelectValue placeholder="No parent role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_PARENT_ROLE_VALUE}>No parent role</SelectItem>
                  {availableRoles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Inherits permissions from parent role
              </p>
            </div>

            {isEditMode && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="isActive">Active</Label>
                  <p className="text-xs text-muted-foreground">Role can be assigned to users</p>
                </div>
                <Switch
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createRole.isPending || updateRole.isPending}
            >
              {createRole.isPending || updateRole.isPending
                ? 'Saving...'
                : isEditMode
                ? 'Update Role'
                : 'Create Role'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
